import React from 'react';
import PropTypes from 'prop-types';
import {viewWithDataGetPrimaryData} from '../box-utils/PrimaryData';
import {ParentBox} from './../box-utils/ParentBox.react.js';
import BoxDecorator from './../box-utils/BoxDecorator.react';
import {FormattedMessage, defineMessages, IntlShape} from 'react-intl';
import PanelColumns from './system/PanelColumns.react';
import StatusIcon from './StatusIcon.react';

const messagesResult = defineMessages({
        Ok:{id: 'scenario.screen.emailCheck.result.Ok', 'defaultMessage': 'Verification passes all checks including Syntax, DNS, MX, Mailbox, Deep Server Configuration, Grey Listing.'},
        Bad:{id: 'scenario.screen.emailCheck.result.Bad', 'defaultMessage': 'Verification fails checks for definitive reasons.'},
        RetryLater:{id: 'scenario.screen.emailCheck.result.RetryLater', 'defaultMessage': 'Conclusive verification result cannot be achieved at this time.'},
        Unverifiable:{id: 'scenario.screen.emailCheck.result.Unverifiable', 'defaultMessage': 'Conclusive verification result cannot be achieved.'},
});
const messagesReason = defineMessages({
    AtSignNotFound: {id: 'scenario.screen.emailCheck.reason.AtSignNotFound', 'defaultMessage': 'The required ‘@’ sign is not found in email address.'},
    DomainIsInexistent:{id: 'scenario.screen.emailCheck.reason.DomainIsInexistent', 'defaultMessage': 'The domain defined in the email address does not exist.'},
    DomainIsWellKnownDea:{id: 'scenario.screen.emailCheck.reason.DomainIsWellKnownDea', 'defaultMessage': 'The domain is a well known Disposable Email Address.'},
    GreyListing:{id: 'scenario.screen.emailCheck.reason.GreyListing', 'defaultMessage': 'Grey Listing is in operation. It is not possible to validate email boxes in real-time where grey listing is in operation.'},
    MailboxFull: {id: 'scenario.screen.emailCheck.reason.MailboxFull', 'defaultMessage': 'The mailbox is full.'},
    MailboxDoesNotExist:{id: 'scenario.screen.emailCheck.reason.MailboxDoesNotExist', 'defaultMessage': 'The mailbox does not exist.'},
    NoMxServersFound: {id: 'scenario.screen.emailCheck.reason.NoMxServersFound', 'defaultMessage': 'There are no mail servers defined for this domain.'},
    ServerDoesNotSupportInternationalMailboxes: {id: 'scenario.screen.emailCheck.reason.ServerDoesNotSupportInternationalMailboxes', 'defaultMessage': 'The server does not support international mailboxes.'},
    ServerIsCatchAll: {id: 'scenario.screen.emailCheck.reason.ServerIsCatchAll', 'defaultMessage': 'The server is configured for catch all and responds to all email verifications with a status of Ok.'},
    Success: {id: 'scenario.screen.emailCheck.reason.Success', 'defaultMessage': 'Successful verification.'},
    TooManyAtSignsFound: {id: 'scenario.screen.emailCheck.reason.TooManyAtSignsFound', 'defaultMessage': 'Too many ‘@’ signs found in email address.'},
    Unknown: {id: 'scenario.screen.emailCheck.reason.Unknown', 'defaultMessage': 'The reason for the verification result is unknown.'},
    TransientNetworkFault: {id: 'scenario.screen.emailCheck.reason.TransientNetworkFault', 'defaultMessage': 'A temporary network fault occurred during verification.'},
    PossibleSpamTrapDetected: {id: 'scenario.screen.emailCheck.reason.PossibleSpamTrapDetected', 'defaultMessage': 'A possible spam trap email address or domain has been detected.'}
});
const messagesReasonShort = defineMessages({
    AtSignNotFound: {id: 'scenario.screen.emailCheck.reason.short.AtSignNotFound', 'defaultMessage': '‘@’ not found'},
    DomainIsInexistent:{id: 'scenario.screen.emailCheck.reason.short.DomainIsInexistent', 'defaultMessage': 'Domain does not exist'},
    DomainIsWellKnownDea:{id: 'scenario.screen.emailCheck.reason.short.DomainIsWellKnownDea', 'defaultMessage': 'The domain is DEA'},
    GreyListing:{id: 'scenario.screen.emailCheck.reason.short.GreyListing', 'defaultMessage': 'Grey Listing'},
    MailboxFull: {id: 'scenario.screen.emailCheck.reason.short.MailboxFull', 'defaultMessage': 'Mailbox full.'},
    MailboxDoesNotExist:{id: 'scenario.screen.emailCheck.reason.short.MailboxDoesNotExist', 'defaultMessage': 'Mailbox does not exist.'},
    NoMxServersFound: {id: 'scenario.screen.emailCheck.reason.short.NoMxServersFound', 'defaultMessage': 'No mail servers'},
    ServerDoesNotSupportInternationalMailboxes: {id: 'scenario.screen.emailCheck.reason.short.ServerDoesNotSupportInternationalMailboxes', 'defaultMessage': 'Server does not support international mailboxes.'},
    ServerIsCatchAll: {id: 'scenario.screen.emailCheck.reason.short.ServerIsCatchAll', 'defaultMessage': 'Server is catch all'},
    Success: {id: 'scenario.screen.emailCheck.reason.short.Success', 'defaultMessage': 'Successful verification.'},
    TooManyAtSignsFound: {id: 'scenario.screen.emailCheck.reason.short.TooManyAtSignsFound', 'defaultMessage': 'Too many ‘@’ found'},
    Unknown: {id: 'scenario.screen.emailCheck.reason.short.Unknown', 'defaultMessage': 'Unknown reason'},
    TransientNetworkFault: {id: 'scenario.screen.emailCheck.reason.short.TransientNetworkFault', 'defaultMessage': 'Transient network fault'},
    PossibleSpamTrapDetected: {id: 'scenario.screen.emailCheck.reason.short.PossibleSpamTrapDetected', 'defaultMessage': 'Possible spam trap detected'}
});

export default class EmailCheck extends ParentBox {
	static handlerName = "email-check";

    static propTypes = {
        intl: PropTypes.object.isRequired,
        isPreview: PropTypes.bool,
        viewWithData: PropTypes.object.isRequired,
    };

	getIcon() {
        return 'fa-at';
    }

	getServiceName() {
		if (this.props.viewWithData.view.entityViewDef) {
            return this.props.viewWithData.view.entityViewDef.title;
        } else {
            return '';
        }
	}

	getPreviewData() {
		throw new Error('You have to implement the method getPreviewData!');
	}

	renderServerLocation(emailValue) {
		if (emailValue["com.dativery.model.entities.countries@countryCodeIso31661Alpha2"]) {
			return (
				<span><FormattedMessage defaultMessage="Mail server country:" id="scenario.screen.emailCheck.mailServerCountry" /> {emailValue["com.dativery.model.entities.countries@countryCodeIso31661Alpha2"]}</span>
			);
		}

		return null;
	}

	getMessageReason(reason) {
        if (reason in messagesReason) {
            return this.props.intl.formatMessage(messagesReason[reason]);
        }
        return undefined;
    }

    getMessageReasonShort(reason) {
        if (reason in messagesReasonShort) {
            return this.props.intl.formatMessage(messagesReasonShort[reason]);
        }
        return undefined;
    }

	renderStatus(emailValue) {
        let msg = "?";

        if (messagesResult[emailValue["com.emailhippo.emailAddress@result"]]) {
            msg = this.props.intl.formatMessage(messagesResult[emailValue["com.emailhippo.emailAddress@result"]]);
        } else {

        }

		if (!Boolean(emailValue["com.emailhippo.emailAddress@result"]) || emailValue["com.emailhippo.emailAddress@result"] === 'Ok') {
                return (<span><StatusIcon title={this.props.intl.formatMessage(messagesResult["Ok"])} type={StatusIcon.OK} /></span>);
		}
        if (emailValue["com.emailhippo.emailAddress@result"] === 'Bad') {
            return (<span><StatusIcon title={msg} type={StatusIcon.ERROR} /><br /><FormattedMessage defaultMessage="Problem:" id="scenario.screen.emailCheck.problem" /> <span title={this.getMessageReason(emailValue["com.emailhippo.emailAddress@reason"])}>{this.getMessageReasonShort(emailValue["com.emailhippo.emailAddress@reason"])}</span></span>);
        }

        return (<span><StatusIcon title={msg} type={StatusIcon.HELP} /><br /><FormattedMessage defaultMessage="Problem:" id="scenario.screen.emailCheck.problem" /> <span title={this.getMessageReason(emailValue["com.emailhippo.emailAddress@reason"])}>{this.getMessageReasonShort(emailValue["com.emailhippo.emailAddress@reason"])}</span></span>);
	}

	renderEmail(emailValue, idx) {
        if (emailValue["com.dativery.model.entities.emailAddress@emailAddress"]) {
            return (
                <div key={idx}>
                    <strong>{emailValue["com.dativery.model.entities.emailAddress@emailAddress"]}</strong> {this.renderStatus(emailValue)}<br/>
                    {this.renderServerLocation(emailValue)}
                </div>
            );
        } else {
            return null;
        }
	}

	render() {
		let data = this.props.isPreview ? this.getPreviewData() : viewWithDataGetPrimaryData(this.props.viewWithData);

        return (
            <BoxDecorator className="email-check" icon={this.getIcon()} isPreview={this.props.isPreview} padding title={this.getServiceName()}>
				<PanelColumns className="boxOneTimeFontSize">
					{data.map((emailValue, idx) => {
                        return this.renderEmail(emailValue, idx);
					})}
				</PanelColumns>
            </BoxDecorator>
        );
	}
}
